import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import {
  DashHeader,
  Signin,
  Verify,
  Footer,
  Jobs,
  LoadingOverlay,
  Toast,
  Banner,
  StripePaymentModal,
} from '../components';
import ExtendRenewModal from '../components/ExtendRenewModal';
import PromoCodeForm from '../components/PromoCodeForm';
import { creditsQuery } from '../utils/Data';
import { clientNoCdn } from '../clientNoCdn';
import { ExclamationCircleIcon, GiftIcon } from '@heroicons/react/solid';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC);

function Dashboard() {
  const { currentUser, signout, verifyEmail } = useAuth();
  const [error, setError] = useState('');
  const [credits, setCredits] = useState(0);
  const [legacyBalance, setLegacyBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showExtendRenewModal, setShowExtendRenewModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isRenewAction, setIsRenewAction] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshJobs, setRefreshJobs] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [pendingDuration, setPendingDuration] = useState(null);
  const [pendingBalanceApplied, setPendingBalanceApplied] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [creditInfo, setCreditInfo] = useState(null); // GROWTH-001/002: promo + referral summary

  const navigate = useNavigate();

  function handleSignout(e) {
    setError('');

    try {
      signout();
      navigate('/');
    } catch {
      setErrorMessage('Unable to sign out. Please try again.');
      setShowErrorModal(true);
    }
  }

  async function checkVerificationStatus() {
    setCheckingVerification(true);
    try {
      // Reload the current user from Firebase to get fresh auth state
      await currentUser.reload();
      // Force a page reload to get updated currentUser from AuthContext
      window.location.reload();
    } catch (err) {
      console.error('Error checking verification:', err);
      setCheckingVerification(false);
    }
  }

  async function handleResendEmail() {
    setResendingEmail(true);
    try {
      await verifyEmail();
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000); // Hide success message after 5 seconds
      setResendingEmail(false);
    } catch (err) {
      console.error('Error resending email:', err);
      setErrorMessage('Unable to resend verification email. Please try again.');
      setShowErrorModal(true);
      setResendingEmail(false);
    }
  }

  function handleExtendClick(jobData) {
    setSelectedJob(jobData);
    setIsRenewAction(false);
    setShowExtendRenewModal(true);
  }

  function handleRenewClick(jobData) {
    setSelectedJob(jobData);
    setIsRenewAction(true);
    setShowExtendRenewModal(true);
  }

  async function refreshBalance() {
    const res = await clientNoCdn.fetch(creditsQuery(currentUser.uid));
    if (res && res.length > 0) setLegacyBalance(res[0].legacyBalance || 0);
  }

  function handlePromoApplied(result) {
    setCreditInfo((prev) => ({ ...(prev || {}), promoEligible: false, pendingPromoCode: null }));
    setSuccessMessage(`Promo code ${result.code} applied: $${Number(result.credit).toFixed(2)} credit added to your account.`);
    setShowSuccessToast(true);
    refreshBalance().catch(() => {});
  }

  // Promo + referral summary once the account is verified. A promo code entered at sign-up is
  // redeemed here, the first time the verified dashboard opens (the server re-checks everything).
  useEffect(() => {
    if (!currentUser || !currentUser.emailVerified || !profileExists) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post('/.netlify/functions/employerCredits', { action: 'summary' });
        if (cancelled) return;
        const info = res.data.result;
        setCreditInfo(info);
        if (info && info.pendingPromoCode) {
          try {
            const redeemed = await axios.post('/.netlify/functions/employerCredits', { action: 'redeemPromo' });
            if (!cancelled) handlePromoApplied(redeemed.data.result);
          } catch (err) {
            if (cancelled) return;
            setCreditInfo((prev) => ({ ...(prev || {}), pendingPromoCode: null }));
            const text = err && err.response && err.response.data && err.response.data.error;
            setErrorMessage(text ? `Your promo code couldn't be applied: ${text}` : "Your promo code couldn't be applied. Please try again later.");
            setShowErrorModal(true);
          }
        }
      } catch (err) {
        console.error('Credit summary unavailable', err && err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, profileExists]);

  async function handleConfirmExtendRenew(duration, amountDue, balanceApplied) {
    try {
      if (amountDue > 0) {
        // Create payment intent and open Stripe payment modal
        const response = await axios.post('/.netlify/functions/createExtendRenewPaymentIntent', {
          amount: amountDue,
          employerId: currentUser.uid,
          jobData: selectedJob,
          duration: duration,
          legacyBalance: balanceApplied,
          isRenew: isRenewAction
        });

        setClientSecret(response.data.clientSecret);
        setPendingDuration(duration);
        setPendingBalanceApplied(balanceApplied);
        setShowExtendRenewModal(false);
        setShowPaymentModal(true);
      } else {
        // Process with legacy balance only (no payment needed)
        await processExtendRenew(duration, balanceApplied, null);
      }
    } catch (error) {
      console.error('Error extending/renewing job:', error);
      setErrorMessage('Failed to initiate payment. Please try again.');
      setShowErrorModal(true);
    }
  }

  async function handlePaymentSuccess(paymentIntent) {
    setShowPaymentModal(false);
    await processExtendRenew(pendingDuration, pendingBalanceApplied, paymentIntent.id);
  }

  async function processExtendRenew(duration, balanceApplied, paymentIntentId) {
    try {
      const response = await axios.post('/.netlify/functions/extendOrRenewJob', {
        jobId: selectedJob.jobId,
        employerId: currentUser.uid,
        duration: duration,
        isRenew: isRenewAction,
        legacyBalanceUsed: balanceApplied,
        paymentIntentId: paymentIntentId
      });

      if (response.data.success) {
        setSuccessMessage(`Job ${isRenewAction ? 'renewed' : 'extended'} successfully!`);
        setShowSuccessToast(true);

        // Refresh balance
        const res = await clientNoCdn.fetch(creditsQuery(currentUser.uid));
        if (res && res.length > 0) {
          setLegacyBalance(res[0].legacyBalance || 0);
        }

        // Trigger jobs refresh after 2 seconds to allow Sanity propagation
        setTimeout(() => {
          setRefreshJobs(prev => prev + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error processing extend/renew:', error);
      setErrorMessage(`Failed to ${isRenewAction ? 'renew' : 'extend'} job. Please try again.`);
      setShowErrorModal(true);
    }
  }

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    clientNoCdn
      .fetch(creditsQuery(currentUser.uid))
      .then((res) => {
        if (res && res.length > 0) {
          setCredits(res[0].postCredits || 0);
          setLegacyBalance(res[0].legacyBalance || 0);
          setProfileExists(true);
        } else {
          setProfileExists(false);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setProfileExists(false);
        setLoading(false);
      });
  }, [currentUser]);

  if (!currentUser && !loading) {
    return <Navigate to="/sign-in" replace />;
  }

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!currentUser) {
    return <Navigate to="/sign-in" replace />;
  } else if (currentUser && !currentUser.emailVerified) {
    return (
      <>
        <DashHeader handle={handleSignout} userEmail={currentUser?.email} />

        <div className="flex justify-center px-4 py-16 lg:py-60">
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationCircleIcon
                    className="h-6 w-6 text-primary"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Email Verification Required
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Please verify your email to access the dashboard. After verifying your email, click "Check Verification Status" below to refresh.
                    </p>
                    {!profileExists && (
                      <p className="text-sm text-gray-500 mt-2">
                        If you haven't created your profile yet, click "Create Profile" to continue.
                      </p>
                    )}
                    {emailSent && (
                      <p className="text-sm text-green-600 font-medium mt-2">
                        ✓ Verification email sent! Check your inbox.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={checkVerificationStatus}
                disabled={checkingVerification}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingVerification ? 'Checking...' : 'Check Verification Status'}
              </button>
              {profileExists ? (
                <button
                  type="button"
                  onClick={handleResendEmail}
                  disabled={resendingEmail || emailSent}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingEmail ? 'Sending...' : emailSent ? 'Email Sent!' : 'Resend Verification Email'}
                </button>
              ) : (
                <Link to="/create-profile">
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Create Profile
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <DashHeader handle={handleSignout} userEmail={currentUser?.email} />

      {showBanner && (
        <Banner
          legacyBalance={legacyBalance}
          postCredits={credits}
          onDismiss={() => setShowBanner(false)}
        />
      )}

      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 flex flex-col gap-3 lg:flex-row justify-center lg:justify-between items-center mt-4 mb-6">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
          <Link to="/dashboard/referrals" className="inline-flex items-center font-medium text-gray-700 hover:text-primary">
            <GiftIcon className="mr-1.5 h-5 w-5 text-primary" aria-hidden="true" />
            Refer a business: you each get ${(creditInfo && creditInfo.rewardAmount) || 10} credit
          </Link>
          {creditInfo && creditInfo.promoEligible && !creditInfo.pendingPromoCode && <PromoCodeForm onApplied={handlePromoApplied} />}
        </div>
        {Number(legacyBalance) > 0 && (
          <p className="inline-flex flex-wrap items-center justify-center rounded-md border border-red-100 bg-red-50 px-4 py-2 text-sm text-gray-800">
            Account balance:
            <span className="ml-1 font-semibold text-primary">
              ${Number(legacyBalance).toFixed(2)}
            </span>
            <span className="ml-2 text-gray-500">
              Applied automatically to your next posting, extension or renewal.
            </span>
          </p>
        )}
      </div>

      <div className="flex-grow flex flex-col">
        <Jobs
          onExtendClick={handleExtendClick}
          onRenewClick={handleRenewClick}
          refreshTrigger={refreshJobs}
        />
      </div>

      {selectedJob && (
        <ExtendRenewModal
          isOpen={showExtendRenewModal}
          onClose={() => setShowExtendRenewModal(false)}
          onConfirm={handleConfirmExtendRenew}
          jobData={selectedJob}
          isExpired={isRenewAction}
          legacyBalance={legacyBalance}
        />
      )}

      {clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <StripePaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={handlePaymentSuccess}
            amount={(() => {
              const PRICING = { 91: 30, 181: 50, 365: 60 };
              const pricePerBoard = PRICING[pendingDuration];
              const boardCount = selectedJob?.postAt?.length || 0;
              const total = boardCount * pricePerBoard;
              return Math.max(0, total - pendingBalanceApplied);
            })()}
            clientSecret={clientSecret}
            userEmail={currentUser?.email}
          />
        </Elements>
      )}

      <Toast
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorMessage}
        type="error"
      />

      <Toast
        isOpen={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage}
        type="success"
      />

      <Footer />
    </div>
  );
}

export default Dashboard;
